import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Calendar,
  Clock,
  MapPin,
  X,
  Check,
  Loader2,
  User,
  Mail,
  FileText,
  MessageSquare,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";

// Constants - Country Codes with Flag Emojis
export const COUNTRY_CODES = [
  { name: "United States", code: "+1", country: "US", flag: "🇺🇸", isPopular: true },
  { name: "India", code: "+91", country: "IN", flag: "🇮🇳", isPopular: true },
  { name: "United Kingdom", code: "+44", country: "GB", flag: "🇬🇧", isPopular: true },
  { name: "Canada", code: "+1", country: "CA", flag: "🇨🇦", isPopular: true },
  { name: "Australia", code: "+61", country: "AU", flag: "🇦🇺", isPopular: true },
  { name: "United Arab Emirates", code: "+971", country: "AE", flag: "🇦🇪", isPopular: true },
  { name: "Saudi Arabia", code: "+966", country: "SA", flag: "🇸🇦", isPopular: true },
  { name: "Germany", code: "+49", country: "DE", flag: "🇩🇪", isPopular: false },
  { name: "France", code: "+33", country: "FR", flag: "🇫🇷", isPopular: false },
  { name: "Italy", code: "+39", country: "IT", flag: "🇮🇹", isPopular: false },
  { name: "Spain", code: "+34", country: "ES", flag: "🇪🇸", isPopular: false },
  { name: "China", code: "+86", country: "CN", flag: "🇨🇳", isPopular: false },
  { name: "Japan", code: "+81", country: "JP", flag: "🇯🇵", isPopular: false },
  { name: "Russia", code: "+7", country: "RU", flag: "🇷🇺", isPopular: false },
  { name: "Brazil", code: "+55", country: "BR", flag: "🇧🇷", isPopular: false },
  { name: "South Korea", code: "+82", country: "KR", flag: "🇰🇷", isPopular: false },
  { name: "Singapore", code: "+65", country: "SG", flag: "🇸🇬", isPopular: false },
  { name: "Switzerland", code: "+41", country: "CH", flag: "🇨🇭", isPopular: false },
  { name: "Netherlands", code: "+31", country: "NL", flag: "🇳🇱", isPopular: false },
  { name: "South Africa", code: "+27", country: "ZA", flag: "🇿🇦", isPopular: false },
  { name: "Indonesia", code: "+62", country: "ID", flag: "🇮🇩", isPopular: false },
  { name: "Malaysia", code: "+60", country: "MY", flag: "🇲🇾", isPopular: false },
  { name: "Pakistan", code: "+92", country: "PK", flag: "🇵🇰", isPopular: false },
  { name: "Thailand", code: "+66", country: "TH", flag: "🇹🇭", isPopular: false },
  { name: "Philippines", code: "+63", country: "PH", flag: "🇵🇭", isPopular: false },
  { name: "Vietnam", code: "+84", country: "VN", flag: "🇻🇳", isPopular: false },
  { name: "Bangladesh", code: "+880", country: "BD", flag: "🇧🇩", isPopular: false },
  { name: "Sri Lanka", code: "+94", country: "LK", flag: "🇱🇰", isPopular: false },
  { name: "Nepal", code: "+977", country: "NP", flag: "🇳🇵", isPopular: false },
  { name: "Myanmar", code: "+95", country: "MM", flag: "🇲🇲", isPopular: false },
  { name: "Turkey", code: "+90", country: "TR", flag: "🇹🇷", isPopular: false },
  { name: "Egypt", code: "+20", country: "EG", flag: "🇪🇬", isPopular: false },
  { name: "Nigeria", code: "+234", country: "NG", flag: "🇳🇬", isPopular: false },
  { name: "Kenya", code: "+254", country: "KE", flag: "🇰🇪", isPopular: false },
  { name: "Ghana", code: "+233", country: "GH", flag: "🇬🇭", isPopular: false },
  { name: "Mexico", code: "+52", country: "MX", flag: "🇲🇽", isPopular: false },
  { name: "Argentina", code: "+54", country: "AR", flag: "🇦🇷", isPopular: false },
  { name: "Chile", code: "+56", country: "CL", flag: "🇨🇱", isPopular: false },
  { name: "Colombia", code: "+57", country: "CO", flag: "🇨🇴", isPopular: false },
  { name: "Peru", code: "+51", country: "PE", flag: "🇵🇪", isPopular: false },
  { name: "Ecuador", code: "+593", country: "EC", flag: "🇪🇨", isPopular: false },
  { name: "Venezuela", code: "+58", country: "VE", flag: "🇻🇪", isPopular: false },
  { name: "Uruguay", code: "+598", country: "UY", flag: "🇺🇾", isPopular: false },
  { name: "Paraguay", code: "+595", country: "PY", flag: "🇵🇾", isPopular: false },
  { name: "Bolivia", code: "+591", country: "BO", flag: "🇧🇴", isPopular: false },
  { name: "Guyana", code: "+592", country: "GY", flag: "🇬🇾", isPopular: false },
  { name: "Suriname", code: "+597", country: "SR", flag: "🇸🇷", isPopular: false },
  { name: "French Guiana", code: "+594", country: "GF", flag: "🇬🇫", isPopular: false },
];

// Phone validation rules for different countries — patterns only; messages resolved at runtime via t()
export const PHONE_VALIDATION_PATTERNS = {
  "+1": /^[0-9]{10}$/,
  "+91": /^[0-9]{10}$/,
  "+44": /^[0-9]{10}$/,
  "+61": /^[0-9]{10}$/,
  "+971": /^[0-9]{10}$/,
  "+966": /^[0-9]{10}$/,
  "+49": /^[0-9]{10}$/,
  "+33": /^[0-9]{10}$/,
  "+39": /^[0-9]{10}$/,
  "+34": /^[0-9]{10}$/,
  "+86": /^[0-9]{10}$/,
  "+81": /^[0-9]{10}$/,
  "+7": /^[0-9]{10}$/,
  "+55": /^[0-9]{10}$/,
  "+82": /^[0-9]{10}$/,
  "+65": /^[0-9]{10}$/,
  "+92": /^[0-9]{10}$/,
  "+880": /^[0-9]{10}$/,
  default: /^[0-9]{10}$/,
};

export const DEFAULT_FORM_VALUES = {
  name: "",
  email: "",
  countryCode: "+1", // Default to US; auto-detected from the visitor's location on open
  phone: "",
  purpose: "",
  preferredTime: [],
  additionalInfo: "",
  partySize: 1,
  attendees: [{ name: "" }],
  custom: [], // answers to an event's organizer-defined registration questions
};

// Appointment Request Modal Component
export default function AppointmentRequestModal({ isOpen, onClose, scheduleItem, onSubmit }) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  // For events: existing registrations found for the entered email (dedupe +
  // "you have already registered" notice + 6-per-email remaining allowance).
  const [priorInfo, setPriorInfo] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  // PURPOSE_OPTIONS: value = English enum string sent to server; label = translated display text
  const PURPOSE_OPTIONS = [
    { value: "Personal Guidance", label: t("schedule.purpose.personalGuidance") },
    { value: "Spiritual Discussion", label: t("schedule.purpose.spiritualDiscussion") },
    { value: "Community Event", label: t("schedule.purpose.communityEvent") },
    { value: "Organization Collaboration", label: t("schedule.purpose.organizationCollaboration") },
    { value: "Media Interview", label: t("schedule.purpose.mediaInterview") },
    {
      value: "Educational Institution Visit",
      label: t("schedule.purpose.educationalInstitutionVisit"),
    },
    { value: "Cultural Program", label: t("schedule.purpose.culturalProgram") },
    { value: "Charitable Work Discussion", label: t("schedule.purpose.charitableWorkDiscussion") },
    { value: "Other", label: t("schedule.purpose.other") },
  ];

  // Validation schema — created per-render so t() is in scope for translated messages
  // Accepts the active scheduleItem so event-specific rules can be applied conditionally
  const createValidationSchema = (scheduleItem) => {
    const captureParty = true; // capture Number of People + attendee names for every schedule type
    // Events have a fixed date/venue, so the meeting-only fields (purpose,
    // preferred time) are neither shown nor validated for them.
    const isEventType = scheduleItem?.type === "event";
    // Events cap party size at 6 per email (also bounded by seats left);
    // meetings keep the wider bound.
    const maxParty = isEventType
      ? Math.max(1, Math.min(6, scheduleItem?.availableSeats ?? 1))
      : Math.max(1, Math.min(25, scheduleItem?.availableSeats ?? 1));

    return yup.object().shape({
      name: yup
        .string()
        .required(t("schedule.validation.nameRequired"))
        .min(3, t("schedule.validation.nameMinLength"))
        .max(50, t("schedule.validation.nameMaxLength"))
        .matches(/^[a-zA-Z\s.'-]+$/, t("schedule.validation.namePattern")),
      email: yup
        .string()
        .required(t("schedule.validation.emailRequired"))
        .email(t("schedule.validation.emailValid"))
        .matches(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          t("schedule.validation.emailValid")
        ),
      countryCode: yup.string().required(t("schedule.validation.countryCodeRequired")),
      phone: yup
        .string()
        .required(t("schedule.validation.phoneRequired"))
        .test("phone-validation", t("schedule.validation.phoneInvalid"), function (value) {
          const countryCode = this.parent.countryCode;
          const pattern =
            PHONE_VALIDATION_PATTERNS[countryCode] || PHONE_VALIDATION_PATTERNS.default;

          if (!pattern.test(value)) {
            return this.createError({
              path: this.path,
              message: t("schedule.validation.phone10Digits"),
            });
          }
          return true;
        }),
      purpose: isEventType
        ? yup.string().notRequired()
        : yup
            .string()
            .required(t("schedule.validation.purposeRequired"))
            .notOneOf([""], t("schedule.validation.purposeSelect")),
      preferredTime: isEventType
        ? yup.array().notRequired()
        : yup.array().min(1, t("schedule.validation.preferredTimeRequired")),
      additionalInfo: yup.string().max(500, t("schedule.validation.additionalInfoMax")),
      custom: yup
        .array()
        .of(
          yup.object({
            label: yup.string(),
            value: yup.string().max(300, "Max 300 characters"),
          })
        )
        .test("required-custom-questions", "required", function (arr) {
          if (!isEventType) return true;
          const fields = scheduleItem?.registrationFields || [];
          for (let i = 0; i < fields.length; i++) {
            if (fields[i]?.required && !(arr?.[i]?.value || "").trim()) {
              return this.createError({
                path: `custom[${i}].value`,
                message: `${fields[i].label} is required`,
              });
            }
          }
          return true;
        }),
      partySize: captureParty
        ? yup
            .number()
            .typeError("Enter a number")
            .required("Party size is required")
            .min(1, "At least 1 person")
            .max(maxParty, `Only ${maxParty} seat${maxParty === 1 ? "" : "s"} left`)
        : yup.number().notRequired(),
      attendees: captureParty
        ? yup
            .array()
            .of(yup.object({ name: yup.string().trim().required("Name is required") }))
            .min(1)
            .test("matches-party", "Add a name for each attendee", function (v) {
              return Array.isArray(v) && v.length === Number(this.parent.partySize);
            })
        : yup.array().notRequired(),
    });
  };

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(createValidationSchema(scheduleItem)),
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const watchedCountryCode = watch("countryCode");
  const watchedPreferredTime = watch("preferredTime") || [];

  // Whether this modal is registering for a public EVENT (fixed date/venue)
  // rather than requesting a personal meeting — events swap the meeting-only
  // fields for the organizer-defined registration questions.
  const isEventForm = scheduleItem?.type === "event";

  // Whether to show the live remaining-seats count. Events can hide it via the
  // CMS "Show remaining seats" toggle (showSeatsRemaining === false); meetings
  // and any item without the flag always show it.
  const showSeats = scheduleItem?.showSeatsRemaining !== false;

  // A single email may bring at most 6 people to an event, and no more than the
  // seats left. Once we know this email's prior registrations, the remaining
  // allowance shrinks accordingly (and hits 0 when they've used all 6).
  const eventSeatMax = Math.max(1, Math.min(6, scheduleItem?.availableSeats ?? 1));
  const effectiveMax =
    isEventForm && priorInfo
      ? Math.max(0, Math.min(eventSeatMax, priorInfo.remaining ?? eventSeatMax))
      : eventSeatMax;
  const limitReached = isEventForm && priorInfo && (priorInfo.remaining ?? 1) <= 0;

  // Ask the server whether this email OR phone already registered for this
  // event. Checking the phone too catches the commonest duplicate — the same
  // person returning with a second email address — which an email-only lookup
  // misses entirely.
  const checkExistingIdentity = useCallback(
    async (emailValue, phoneValue) => {
      const email = (emailValue ?? getValues("email") ?? "").trim();
      const rawPhone = (phoneValue ?? getValues("phone") ?? "").toString().trim();
      const code = getValues("countryCode") || "";
      const phone = rawPhone ? `${code}${rawPhone}` : "";

      const hasEmail = /^\S+@\S+\.\S+$/.test(email);
      const hasPhone = phone.replace(/\D/g, "").length >= 8;

      if (!isEventForm || !scheduleItem?.id || (!hasEmail && !hasPhone)) {
        setPriorInfo(null);
        return;
      }
      try {
        setCheckingEmail(true);
        const res = await axios.get(`${import.meta.env.VITE_APP_API_BASE_URL}/lookup`, {
          params: {
            scheduleId: scheduleItem.id,
            ...(hasEmail ? { email } : {}),
            ...(hasPhone ? { phone } : {}),
          },
        });
        const data = res.data?.data;
        setPriorInfo(data && (data.registrations || []).length ? data : null);
      } catch {
        setPriorInfo(null);
      } finally {
        setCheckingEmail(false);
      }
    },
    [isEventForm, scheduleItem, getValues]
  );

  // Reset form when modal opens (pre-seeding one answer row per event question)
  useEffect(() => {
    if (isOpen) {
      const fields = scheduleItem?.registrationFields || [];
      reset({
        ...DEFAULT_FORM_VALUES,
        custom: fields.map((field) => ({ label: field.label, value: "" })),
      });
      setError("");
      setSuccessMessage("");
      setIsSubmitting(false);
      setPriorInfo(null);
    }
  }, [isOpen, reset, scheduleItem]);

  // Escape closes the form, and the page behind is frozen while it is open so
  // a scroll gesture inside the modal cannot run away into the page under it.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  // Auto-detect the visitor's country for the phone code (best-effort; falls back to +1).
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("https://ipwho.is/?fields=country_code");
        const data = await res.json();
        const iso = data && data.country_code; // e.g. "US", "IN", "GB"
        if (!iso || cancelled) return;
        const match = COUNTRY_CODES.find((c) => c.country === iso);
        if (match) setValue("countryCode", match.code, { shouldValidate: false });
      } catch {
        /* keep the +1 fallback */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, setValue]);

  // Get phone validation info for current country
  const getPhoneValidationInfo = useCallback(
    (countryCode) => {
      const pattern = PHONE_VALIDATION_PATTERNS[countryCode] || PHONE_VALIDATION_PATTERNS.default;
      const countryInfo = COUNTRY_CODES.find((c) => c.code === countryCode);

      return {
        placeholder: t("schedule.phone.placeholder10Digit"),
        pattern,
        countryName: countryInfo?.name || "phone",
      };
    },
    [t]
  );

  // Handle checkbox changes for preferred time
  const handlePreferredTimeChange = useCallback(
    (value, checked) => {
      const currentValues = watchedPreferredTime;
      if (checked) {
        setValue("preferredTime", [value]); // Only allow one selection
      } else {
        setValue(
          "preferredTime",
          currentValues.filter((v) => v !== value)
        );
      }
    },
    [watchedPreferredTime, setValue]
  );

  // Form submission handler
  const onFormSubmit = useCallback(
    async (data) => {
      setIsSubmitting(true);
      setError("");

      try {
        const formData = new FormData();

        // Format the phone number with country code
        const formattedData = {
          ...data,
          phone: `${data.countryCode}${data.phone}`,
          preferredTime: Array.isArray(data.preferredTime)
            ? data.preferredTime.join(", ")
            : data.preferredTime,
        };

        // Remove separate countryCode since it's now part of phone
        delete formattedData.countryCode;

        // Events don't send the meeting-only fields; their custom answers are
        // appended as indexed custom[i][label]/custom[i][value] pairs below.
        const customAnswers = Array.isArray(formattedData.custom) ? formattedData.custom : [];
        delete formattedData.custom;
        if (scheduleItem?.type === "event") {
          delete formattedData.purpose;
          delete formattedData.preferredTime;
        }

        // Add form fields to FormData
        Object.entries(formattedData).forEach(([key, value]) => {
          formData.append(key, value || "");
        });

        if (scheduleItem?.type === "event") {
          customAnswers.forEach((answer, i) => {
            formData.append(`custom[${i}][label]`, answer?.label || "");
            formData.append(`custom[${i}][value]`, answer?.value || "");
          });
        }

        formData.append("status", "PENDING");
        formData.append("isDeleted", false);
        formData.append("submittedAt", new Date().toISOString());

        // Add requested schedule fields
        // NOTE: sentinel comparisons use the raw English sentinel strings stored in scheduleItem
        // (set during formatScheduleData). These are not translated — they are server-side markers.
        const scheduleData = {
          scheduleId: scheduleItem.id,
          eventDate:
            scheduleItem.startDate !== "Not Available" ? scheduleItem.startDate : "To be announced",
          eventTime:
            scheduleItem.time && scheduleItem.time !== "Time TBD"
              ? scheduleItem.time
              : "To be announced",
          eventLocation: scheduleItem.location || "To be announced",
          eventDetails: scheduleItem.details || "Details to be announced",
        };

        Object.entries(scheduleData).forEach(([key, value]) => {
          formData.append(`requestedSchedule[${key}]`, value);
        });

        // Number of people + attendee names (captured for every schedule type)
        if (data.partySize >= 1) {
          formData.append("partySize", String(data.partySize || 1));
          (data.attendees || []).forEach((a, i) =>
            formData.append(`attendees[${i}][name]`, a?.name || "")
          );
        }

        // Submit the form
        const response = await axios.post(import.meta.env.VITE_APP_API_BASE_URL, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 30000, // 30 second timeout
        });

        setSuccessMessage(t("schedule.appointment.successBody"));

        onSubmit?.({
          ...formattedData,
          status: "PENDING",
          requestedSchedule: scheduleData,
          submittedAt: new Date().toISOString(),
        });

        // Auto close after 5 seconds
        setTimeout(onClose, 5000);
      } catch (err) {
        console.error("Failed to request appointment:", err);

        let errorMessage = t("schedule.appointment.errorGeneric");

        if (err.code === "ECONNABORTED") {
          errorMessage = t("schedule.appointment.errorTimeout");
        } else if (err.response?.status === 409) {
          // Capacity conflict — surface the server message or a sensible default
          errorMessage = err.response?.data?.message || "Registration full — no seats remaining.";
        } else if (err.response?.status === 429) {
          errorMessage = t("schedule.appointment.errorTooMany");
        } else if (err.response?.status >= 500) {
          errorMessage = t("schedule.appointment.errorServer");
        } else if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        }

        setError(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
    [scheduleItem, onSubmit, onClose]
  );

  if (!isOpen) return null;

  return (
    // Tapping the dimmed backdrop closes the form — on a phone that is the
    // gesture people reach for first.
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      role="presentation"
    >
      {/* dvh, not vh: mobile browsers count their address bar in vh, which made
          the panel taller than the visible screen. The panel is a flex column
          so ONLY the body scrolls — previously the whole modal scrolled and the
          header carrying the ✕ slid off the top, leaving no way out. */}
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90dvh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-[#5a0000] to-[#8a0000] text-white p-4 rounded-t-lg flex justify-between items-center shrink-0">
          <h3 className="text-lg font-semibold">
            {scheduleItem?.type === "event" ? "Event Registration" : t("schedule.modal.title")}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
            aria-label={t("schedule.modal.closeAriaLabel")}
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto">
          {successMessage ? (
            <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-4 flex items-start gap-3">
              <div className="bg-green-100 rounded-full p-1 mt-0.5">
                <Check size={16} className="text-green-600" />
              </div>
              <div>
                <p className="font-medium">{t("schedule.modal.success")}</p>
                <p className="text-sm mt-1">{successMessage}</p>
              </div>
            </div>
          ) : (
            <>
              {/* Event Details Summary */}
              <div className="mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="text-sm font-medium text-gray-500 mb-2">
                  {t("schedule.modal.eventDetails")}
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Calendar size={16} className="text-[#e63946]" />
                    <span className="text-gray-700">
                      {scheduleItem.startDate}
                      {scheduleItem.startDate !== scheduleItem.endDate &&
                        scheduleItem.endDate !== "Not Available " && (
                          <span className="ml-1">
                            {t("schedule.modal.to")} {scheduleItem.endDate}
                          </span>
                        )}
                      {scheduleItem.startDate === "Not Available " && (
                        <span className="ml-1 text-xs text-[#e63946]">
                          {t("schedule.modal.toBeAnnounced")}
                        </span>
                      )}
                    </span>
                  </div>
                  {/* Venue wall-clock time — only once an organiser has set it,
                      so meeting requests are unchanged. */}
                  {scheduleItem.time && scheduleItem.time !== "Time TBD" && (
                    <div className="flex items-center gap-2">
                      <Clock size={16} className="text-[#e63946]" />
                      <span className="text-gray-700">{scheduleItem.time}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <MapPin size={16} className="text-[#e63946]" />
                    <span className="text-gray-700">
                      {scheduleItem.location || t("schedule.modal.locationTbd")}
                    </span>
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-2">
                    <div className="bg-red-100 rounded-full p-1 mt-0.5">
                      <X size={16} className="text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium">{t("schedule.modal.errorTitle")}</p>
                      <p className="text-sm mt-1">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Appointment Request Form */}
              <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
                {/* Name Field */}
                <FormField
                  label={t("schedule.modal.form.fullName")}
                  required
                  icon={User}
                  error={errors.name}
                >
                  <input
                    {...register("name")}
                    className={getInputClasses(errors.name)}
                    placeholder={t("schedule.modal.form.fullNamePlaceholder")}
                    autoComplete="name"
                    onChange={(e) => {
                      register("name").onChange(e);
                      // Attendee 1 IS the registrant, so it always tracks this
                      // field — the input below is locked, and mirroring here
                      // unconditionally keeps the two from ever disagreeing.
                      setValue("attendees.0.name", e.target.value, { shouldValidate: false });
                    }}
                  />
                </FormField>

                {/* Email Field */}
                <FormField
                  label={t("schedule.modal.form.emailAddress")}
                  required
                  icon={Mail}
                  error={errors.email}
                >
                  <input
                    {...register("email")}
                    type="email"
                    className={getInputClasses(errors.email)}
                    placeholder={t("schedule.modal.form.emailPlaceholder")}
                    autoComplete="email"
                    onBlur={(e) => {
                      register("email").onBlur(e);
                      checkExistingIdentity(e.target.value, undefined);
                    }}
                  />
                </FormField>

                {/* Already-registered notice (events) — shows what this email
                    has booked and how many more people it may still add. */}
                {isEventForm && priorInfo && (
                  <div
                    className={`rounded-lg border p-3 text-sm ${
                      limitReached
                        ? "border-red-200 bg-red-50 text-red-800"
                        : "border-amber-200 bg-amber-50 text-amber-900"
                    }`}
                  >
                    <p className="font-medium">
                      {priorInfo.matchedByPhoneOnly
                        ? "This phone number is already registered for this event:"
                        : "You have already registered for this event:"}
                    </p>
                    <ul className="mt-1 space-y-1">
                      {priorInfo.registrations.map((r, i) => (
                        <li key={i}>
                          {r.partySize} {r.partySize === 1 ? "person" : "people"}
                          {r.attendees && r.attendees.length
                            ? ` — ${r.attendees.join(", ")}`
                            : ""}{" "}
                          <span className="opacity-70">({r.status})</span>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2">
                      {limitReached
                        ? "You have reached the limit of 6 people per email for this event."
                        : priorInfo.matchedByPhoneOnly
                          ? "If that was you, please add guests to that booking using the link in your confirmation email rather than registering again. If this is a different person, carry on."
                          : `You can register up to ${priorInfo.remaining} more (limit 6 per email). To add guests to your existing booking instead, use the link in your confirmation email.`}
                    </p>
                  </div>
                )}
                {isEventForm && checkingEmail && (
                  <p className="text-xs text-gray-500">Checking your existing registrations…</p>
                )}

                {/* Phone Field with Country Code */}
                <FormField
                  label={t("schedule.modal.form.phoneNumber")}
                  required
                  error={errors.phone || errors.countryCode}
                >
                  {/* text-base (16px) on both controls: iOS Safari auto-zooms
                      the page when a focused field is smaller than 16px, which
                      is what made this row feel broken on an iPhone. */}
                  <div className="flex w-full">
                    <div className="relative shrink-0">
                      <select
                        {...register("countryCode")}
                        className="appearance-none bg-gray-50 border border-gray-300 rounded-l-md pl-2 pr-7 py-2 text-base focus:outline-none focus:ring-2 focus:ring-[#5a0000]/30 w-[88px]"
                      >
                        <optgroup label={t("schedule.modal.popularCountries")}>
                          {COUNTRY_CODES.filter((c) => c.isPopular).map((country) => (
                            <option key={`${country.code}-${country.country}`} value={country.code}>
                              {country.flag} {country.code}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label={t("schedule.modal.allCountries")}>
                          {COUNTRY_CODES.filter((c) => !c.isPopular).map((country) => (
                            <option key={`${country.code}-${country.country}`} value={country.code}>
                              {country.flag} {country.code}
                            </option>
                          ))}
                        </optgroup>
                      </select>
                    </div>
                    {/* The selector alongside already shows the dialling code,
                        so the old prefix inside the box was a duplicate that
                        cost 48px of padding on the narrowest screens. */}
                    <div className="relative min-w-0 flex-1">
                      <input
                        {...register("phone")}
                        type="tel"
                        inputMode="numeric"
                        className={`w-full border-l-0 rounded-l-none px-3 py-2 text-base border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-[#5a0000]/30 ${
                          errors.phone ? "border-red-500" : ""
                        }`}
                        placeholder={getPhoneValidationInfo(watchedCountryCode).placeholder}
                        autoComplete="tel"
                        onBlur={(e) => {
                          register("phone").onBlur(e);
                          // Same duplicate check as the email field — a repeat
                          // devotee is far likelier to reuse their number.
                          checkExistingIdentity(undefined, e.target.value);
                        }}
                      />
                    </div>
                  </div>
                </FormField>

                {/* Purpose Field — meetings only (an event IS the purpose) */}
                {!isEventForm && (
                  <FormField
                    label={t("schedule.modal.form.purposeLabel")}
                    required
                    icon={FileText}
                    error={errors.purpose}
                  >
                    <select {...register("purpose")} className={getInputClasses(errors.purpose)}>
                      <option value="">{t("schedule.modal.form.selectPurpose")}</option>
                      {PURPOSE_OPTIONS.map(({ value: optValue, label: optLabel }) => (
                        <option key={optValue} value={optValue}>
                          {optLabel}
                        </option>
                      ))}
                    </select>
                  </FormField>
                )}

                {/* Preferred Time Field — meetings only (events have a fixed time) */}
                {!isEventForm && (
                  <FormField
                    label={t("schedule.modal.form.preferredTime")}
                    required
                    error={errors.preferredTime}
                  >
                    <div className="pl-6 pt-2 pb-1">
                      {[
                        { value: "Morning", label: t("schedule.modal.form.morning") },
                        { value: "Afternoon", label: t("schedule.modal.form.afternoon") },
                        { value: "Evening", label: t("schedule.modal.form.evening") },
                      ].map(({ value: timeValue, label: timeLabel }) => (
                        <div key={timeValue} className="flex items-center mb-2">
                          <input
                            type="radio"
                            id={`time-${timeValue.toLowerCase()}`}
                            name="preferredTime"
                            value={timeValue}
                            className="w-4 h-4 mr-2 text-[#5a0000] focus:ring-[#5a0000]"
                            checked={watchedPreferredTime.includes(timeValue)}
                            onChange={(e) => handlePreferredTimeChange(timeValue, e.target.checked)}
                          />
                          <label
                            htmlFor={`time-${timeValue.toLowerCase()}`}
                            className="text-sm text-gray-700"
                          >
                            {timeLabel}
                          </label>
                        </div>
                      ))}
                    </div>
                  </FormField>
                )}

                {/* Organizer-defined registration questions (events only) */}
                {isEventForm &&
                  (scheduleItem?.registrationFields || []).map((field, i) => (
                    <div key={`${field.label}-${i}`}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                      </label>
                      {field.type === "select" ? (
                        <select
                          {...register(`custom.${i}.value`)}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#5a0000]/30"
                        >
                          <option value="">{field.placeholder || "Select an option"}</option>
                          {(field.options || []).map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          {...register(`custom.${i}.value`)}
                          maxLength={300}
                          placeholder={field.placeholder || `Enter ${field.label}`}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#5a0000]/30"
                        />
                      )}
                      {errors.custom?.[i]?.value && (
                        <p className="text-red-600 text-xs mt-1">
                          {errors.custom[i].value.message}
                        </p>
                      )}
                    </div>
                  ))}

                {/* Additional Info Field */}
                <FormField
                  label={
                    isEventForm
                      ? "Special requests (optional)"
                      : t("schedule.modal.form.additionalInfo")
                  }
                  icon={MessageSquare}
                  error={errors.additionalInfo}
                  helpText={isEventForm ? undefined : t("schedule.modal.form.additionalInfoHint")}
                >
                  <textarea
                    {...register("additionalInfo")}
                    rows="3"
                    className={getInputClasses(errors.additionalInfo)}
                    placeholder={
                      isEventForm
                        ? "Accessibility needs, questions, or anything we should know"
                        : t("schedule.modal.form.additionalInfoPlaceholder")
                    }
                    maxLength={500}
                  />
                </FormField>

                {/* Number of people + attendee names (all schedule types) */}
                {scheduleItem && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Number of people <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={isEventForm ? effectiveMax : scheduleItem.availableSeats}
                        disabled={limitReached}
                        {...register("partySize", { valueAsNumber: true })}
                        onChange={(e) => {
                          const raw = e.target.value;
                          // Let the field be empty WHILE typing (delete → type a
                          // new number); it is clamped on blur and on submit.
                          if (raw === "") {
                            setValue("partySize", "", { shouldValidate: false });
                            return;
                          }
                          const parsed = parseInt(raw, 10);
                          if (isNaN(parsed)) return;
                          setValue("partySize", parsed);
                          const cap = isEventForm ? effectiveMax : scheduleItem.availableSeats;
                          const n = Math.max(1, Math.min(cap, parsed));
                          const cur = getValues("attendees") || [];
                          const next = Array.from({ length: n }, (_, i) => cur[i] || { name: "" });
                          setValue("attendees", next);
                        }}
                        onBlur={(e) => {
                          const cap = isEventForm ? effectiveMax : scheduleItem.availableSeats;
                          const parsed = parseInt(e.target.value || "1", 10);
                          const n = isNaN(parsed) ? 1 : Math.max(1, Math.min(cap, parsed));
                          setValue("partySize", n, { shouldValidate: true });
                          const cur = getValues("attendees") || [];
                          const next = Array.from({ length: n }, (_, i) => cur[i] || { name: "" });
                          setValue("attendees", next);
                        }}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#5a0000]/30 disabled:bg-gray-100"
                      />
                      {errors.partySize && (
                        <p className="text-red-600 text-xs mt-1">{errors.partySize.message}</p>
                      )}
                      {(showSeats || isEventForm) && (
                        <p className="text-xs text-gray-500 mt-1">
                          {showSeats && (
                            <>
                              {scheduleItem.availableSeats} seat
                              {scheduleItem.availableSeats === 1 ? "" : "s"} remaining
                              {isEventForm ? " · " : ""}
                            </>
                          )}
                          {isEventForm ? "up to 6 per email" : ""}
                        </p>
                      )}
                    </div>
                    {(watch("attendees") || []).map((_, i) => {
                      // Attendee 1 is the registrant themselves, so it is filled
                      // from Full Name above and locked — editing it here would
                      // let the pass name and the booking name drift apart.
                      const isRegistrant = i === 0;
                      return (
                        <div key={i}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Attendee {i + 1} name <span className="text-red-500">*</span>
                          </label>
                          <input
                            {...register(`attendees.${i}.name`)}
                            readOnly={isRegistrant}
                            aria-readonly={isRegistrant}
                            tabIndex={isRegistrant ? -1 : undefined}
                            placeholder={
                              isRegistrant
                                ? "Taken from your full name above"
                                : `Full name of attendee ${i + 1}`
                            }
                            className={`w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#5a0000]/30 ${
                              isRegistrant ? "cursor-not-allowed bg-gray-100 text-gray-500" : ""
                            }`}
                          />
                          {isRegistrant && (
                            <p className="mt-1 text-xs text-gray-500">
                              This is you — it updates automatically from your full name above.
                            </p>
                          )}
                          {errors.attendees?.[i]?.name && (
                            <p className="text-red-600 text-xs mt-1">
                              {errors.attendees[i].name.message}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Custom notes / Terms — organizer-defined, shown as fine print. */}
                {isEventForm && scheduleItem?.registrationNote && (
                  <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-xs leading-5 text-gray-600 whitespace-pre-line">
                    {scheduleItem.registrationNote}
                  </div>
                )}

                {/* Submit Button */}
                <div className="mt-6">
                  <button
                    type="submit"
                    disabled={isSubmitting || limitReached}
                    className="w-full bg-[#5a0000] hover:bg-[#8a0000] text-white py-3 px-4 rounded-md transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center font-medium"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={18} className="animate-spin mr-2" />
                        {t("schedule.modal.form.submitting")}
                      </>
                    ) : limitReached ? (
                      "Registration limit reached"
                    ) : scheduleItem?.type === "event" ? (
                      "Complete Registration"
                    ) : (
                      t("schedule.modal.form.submit")
                    )}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Form Field Component
function FormField({ label, required, icon: Icon, error, helpText, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        {Icon && (
          <Icon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
        )}
        {children}
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error.message}</p>}
      {helpText && !error && <p className="mt-1 text-xs text-gray-500">{helpText}</p>}
    </div>
  );
}

// Utility function for input classes
function getInputClasses(error) {
  const baseClasses =
    "w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#5a0000]/30 transition-colors";
  return error ? `${baseClasses} border-red-500` : `${baseClasses} border-gray-300`;
}
